import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GridWallAccessEnum } from 'api/enums';
import { getStreamToken } from 'api/services/cctv-camera.service';
import { getGridwallStreamToken } from 'api/services/gridwall.service';
import { toast } from 'react-toastify';

const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

const replaceTokenInUrl = (url: string, newToken: string): string => {
    const urlObj = new URL(url);
    urlObj.searchParams.set('token', newToken);
    return urlObj.toString();
};

export interface GridwallStreamSource {
    token: string;
    gridDataId: string;
    access: GridWallAccessEnum;
    accessCode?: string;
}

interface StreamUrlRefreshOptions {
    queryKey: unknown[];
    fetchToken: () => Promise<string>;
    enabled: boolean;
    hlsUrl: string | undefined;
    webrtcUrl: string | undefined;
}

interface StreamUrlRefreshReturn {
    hlsUrl?: string;
    webrtcUrl?: string;
    isLoading: boolean;
    isError: boolean;
    refetch: () => Promise<void>;
}

const useStreamUrlRefresh = ({
    queryKey,
    fetchToken,
    enabled,
    hlsUrl,
    webrtcUrl,
}: StreamUrlRefreshOptions): StreamUrlRefreshReturn => {
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey,
        queryFn: async () => {
            const token = await fetchToken();
            return {
                hlsUrl: hlsUrl ? replaceTokenInUrl(hlsUrl, token) : undefined,
                webrtcUrl: webrtcUrl ? replaceTokenInUrl(webrtcUrl, token) : undefined,
            };
        },
        enabled: enabled && !!(hlsUrl || webrtcUrl),
        refetchInterval: TOKEN_REFRESH_INTERVAL,
        retry: 3,
    });

    const handleRefetch = useCallback(async () => {
        try {
            await refetch();
        } catch (error) {
            console.error('Manual token refresh failed:', error);
            toast.error('Failed to refresh stream token.');
        }
    }, [refetch]);

    return {
        hlsUrl: data?.hlsUrl,
        webrtcUrl: data?.webrtcUrl,
        isLoading,
        isError,
        refetch: handleRefetch,
    };
};

export const useStreamTokenRefresh = (
    projectId: string | undefined,
    cameraId: string | undefined,
    hlsUrl: string | undefined,
    webrtcUrl: string | undefined,
    enabled = true
): StreamUrlRefreshReturn =>
    useStreamUrlRefresh({
        queryKey: ['streamToken', projectId, cameraId],
        fetchToken: () => getStreamToken(projectId!, cameraId!),
        enabled: enabled && !!(projectId && cameraId),
        hlsUrl,
        webrtcUrl,
    });

export const useGridwallStreamTokenRefresh = (
    source: GridwallStreamSource | undefined,
    hlsUrl: string | undefined,
    webrtcUrl: string | undefined
): StreamUrlRefreshReturn =>
    useStreamUrlRefresh({
        queryKey: ['gridwallStreamToken', source?.token, source?.gridDataId],
        fetchToken: () => getGridwallStreamToken(source!.token, source!.gridDataId, source!.accessCode),
        enabled: !!source && (source.access === GridWallAccessEnum.PUBLIC || !!source.accessCode),
        hlsUrl,
        webrtcUrl,
    });
